import {
  Box,
  Button,
  Divider,
  Heading,
  Skeleton,
  SkeletonText,
  Stack,
} from "@chakra-ui/react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import Client, { Item } from "../api/client";
import { Comments } from "./comments";
import { UserModal } from "./user-modal";
import { useParams } from "react-router-dom";

export const Article = () => {
  const params = useParams();
  const queryClient = useQueryClient();
  const [articleKids, setArticleKids] = useState<number[]>([]);

  const articleKidsQueryCache = (
    queryClient.getQueryCache().get(`["article","${params.id}"]`)?.state
      .data as Item
  )?.kids;

  const { data: articleData, isLoading: isArticleLoading } = useQuery({
    queryKey: ["article", params.id],
    queryFn: async () => {
      const result = await Client.getArticleFromId(Number(params.id));

      return result;
    },
  });

  const commentsQuery = useInfiniteQuery({
    queryKey: ["comments-infinite", params.id],
    queryFn: async ({ pageParam }) => {
      let kids: number[] = [];

      if (!pageParam.length) {
        console.log("initial query");
        /**
         * this queryFn is enabled once the article query above is successful
         * articleData must be available at this point
         */
        kids = articleData!.kids.slice(0, 5);
      } else if (articleKids.length) {
        console.log("query in state");
        kids = articleKids.slice(0, 5);
      } else {
        console.log("query from cache");
        /**
         * articleKidsQueryCache holds ALL comment IDs
         * pageParam holds all the comments fetched so far (getNextPageParam)
         * therefore, we only need to grab the comment IDs that come after our cached comments
         */
        kids = articleKidsQueryCache.slice(pageParam.length).slice(0, 5);
      }

      const resultV2 = await Client.getAllComments(kids);
      setArticleKids(articleKids.slice(5));

      return resultV2;
    },
    getNextPageParam: (_, allPages) => {
      const allFetchedComments = allPages.flat();

      if (allFetchedComments.length === articleKidsQueryCache.length) {
        return null;
      }

      /**
       * return all comments fetched so far;
       * allows the next query to use this length to determine the starting point for the next batch of comments
       * */
      return allFetchedComments;
    },
    initialPageParam: [] as Item[],
    enabled: !!articleData && !!articleKids,
  });

  if (isArticleLoading || !articleData) {
    return (
      <Stack paddingTop={"2rem"}>
        <Skeleton height="20px" startColor="hn.background_dark" />
        <br></br>
        <SkeletonText
          startColor="hn.background_dark"
          noOfLines={4}
          spacing="4"
        />
      </Stack>
    );
  }

  const renderComments = () => {
    if (
      commentsQuery.isLoading ||
      !commentsQuery.data ||
      (commentsQuery.isFetching && !commentsQuery.isFetchingNextPage)
    ) {
      return (
        <Stack paddingTop={"2rem"}>
          <Skeleton height="20px" startColor="hn.background_dark" />
          <br></br>
          <SkeletonText
            startColor="hn.background_dark"
            noOfLines={4}
            spacing="4"
          />
        </Stack>
      );
    }

    return (
      <>
        <Comments
          comments={[...commentsQuery.data.pages.flat()]}
          isReplyComment={false}
          indent={0}
        />
        {commentsQuery.hasNextPage ? (
          <Button
            isLoading={commentsQuery.isFetchingNextPage}
            colorScheme="hn"
            loadingText="loading"
            onClick={() => commentsQuery.fetchNextPage()}
          >
            Load more comments
          </Button>
        ) : null}
      </>
    );
  };

  return (
    <Box padding="1em 1em 10em 1em">
      <Box paddingBottom="1rem">
        <Heading as="h1" color="hn.off_white">
          {articleData.title}
        </Heading>
        <Box paddingTop="0.5rem">
          {articleData.score} points | {articleData.descendants} comments |
          by&nbsp;
          <UserModal item={articleData} />
        </Box>
      </Box>
      <Box
        marginBottom="1rem"
        dangerouslySetInnerHTML={{ __html: articleData.text }}
      />
      <Heading as="h2" color="hn.off_white">
        Comments
      </Heading>
      <Divider margin="1rem 0" />
      {renderComments()}
    </Box>
  );
};
